import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import axios, { fehlerText } from '../api/http';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

// Projekttyp-Definition
interface Project {
  id: number;
  name: string;
  client: string;
  address: string;
  start_date: string;
  end_date: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

// Validierungsschema für das Projektformular
const validationSchema = Yup.object({
  name: Yup.string().required('Projektname ist erforderlich'),
  client: Yup.string(),
  address: Yup.string(),
  start_date: Yup.date(),
  end_date: Yup.date()
    .min(Yup.ref('start_date'), 'Enddatum muss nach dem Startdatum liegen, falls angegeben')
    .nullable(),
  status: Yup.string().default('active')
});

const Projects: React.FC = () => {
  // Zustände
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Projekte beim Komponenten-Mount laden
  useEffect(() => {
    fetchProjects();
  }, []);

  // Projekte vom Server laden
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/projects');
      setProjects(response.data.projects);
      setError(null);
    } catch (err) {
      setError(fehlerText(err, 'Fehler beim Laden der Projekte'));
    } finally {
      setLoading(false);
    }
  };

  // Formik-Setup für das Projektformular
  const formik = useFormik({
    initialValues: {
      name: '',
      client: '',
      address: '',
      start_date: '',
      end_date: '',
      status: 'active'
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Stelle sicher, dass die Datumswerte im korrekten Format sind
        const formattedValues = {
          ...values,
          // Stelle sicher, dass leere Datumswerte als null gesendet werden
          start_date: values.start_date || null,
          end_date: values.end_date || null
        };
        
        if (selectedProject) {
          // Projekt aktualisieren
          await axios.put(`/api/projects/${selectedProject.id}`, formattedValues);
          setSuccess('Projekt erfolgreich aktualisiert');
        } else {
          // Neues Projekt erstellen
          await axios.post('/api/projects', formattedValues);
          setSuccess('Projekt erfolgreich erstellt');
        }
        resetForm();
        setDialogOpen(false);
        fetchProjects(); // Projekte neu laden
      } catch (err) {
        setError(fehlerText(err, 'Fehler beim Speichern des Projekts'));
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Dialog zum Erstellen/Bearbeiten eines Projekts öffnen
  const handleOpenDialog = (project?: Project) => {
    if (project) {
      // Werte für die Bearbeitung setzen
      setSelectedProject(project);
      formik.setValues({
        name: project.name,
        client: project.client || '',
        address: project.address || '',
        // Stelle sicher, dass die Datumswerte im Format yyyy-MM-dd sind
        start_date: project.start_date ? project.start_date.substring(0, 10) : '',
        end_date: project.end_date ? project.end_date.substring(0, 10) : '',
        status: project.status || 'active'
      });
    } else {
      // Neues Projekt
      setSelectedProject(null);
      formik.resetForm();
    }
    setDialogOpen(true);
  };

  // Dialog schließen
  const handleCloseDialog = () => {
    setDialogOpen(false);
    formik.resetForm();
  };

  // Dialog zum Löschen eines Projekts öffnen
  const handleOpenDeleteDialog = (project: Project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  // Dialog zum Löschen schließen
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedProject(null);
  };

  // Projekt löschen
  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    try {
      await axios.delete(`/api/projects/${selectedProject.id}`);
      setSuccess('Projekt erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      fetchProjects(); // Projekte neu laden
    } catch (err) {
      setError(fehlerText(err, 'Fehler beim Löschen des Projekts'));
    }
  };

  // Status-Chip rendern
  const renderStatusChip = (status: string) => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';
    let label = status;

    switch (status) {
      case 'active':
        color = 'success';
        label = 'Aktiv';
        break;
      case 'completed':
        color = 'error';
        label = 'Abgeschlossen';
        break;
      case 'planned':
        color = 'warning';
        label = 'Geplant';
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  // Fehlermeldung schließen
  const handleCloseError = () => {
    setError(null);
  };

  // Erfolgsmeldung schließen
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Projekte
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
          >
            Neues Projekt
          </Button>
        </Box>

        {/* Lade-Indikator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Fehlermeldung */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseError} severity="error">
            {error}
          </Alert>
        </Snackbar>

        {/* Erfolgsmeldung */}
        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success">
            {success}
          </Alert>
        </Snackbar>

        {/* Projektliste */}
        {!loading && (
          <>
            {isMobile ? (
              // Mobile Ansicht: Karten
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {projects.map((project) => (
                  <Card 
                    key={project.id} 
                    sx={{ 
                      width: '100%',
                      backgroundColor: theme.palette.background.default
                    }}
                    elevation={2}
                  >
                    <CardHeader
                      title={
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                          {project.name}
                        </Typography>
                      }
                      subheader={
                        project.client && (
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {project.client}
                          </Typography>
                        )
                      }
                      action={
                        <Box sx={{ mt: 1 }}>
                          {renderStatusChip(project.status)}
                        </Box>
                      }
                      sx={{ pb: 1 }}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {project.address && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            backgroundColor: theme.palette.background.paper,
                            p: 1,
                            borderRadius: 1
                          }}>
                            <LocationOnIcon color="action" fontSize="small" />
                            <Typography variant="body2">{project.address}</Typography>
                          </Box>
                        )}
                        {(project.start_date || project.end_date) && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            backgroundColor: theme.palette.background.paper,
                            p: 1,
                            borderRadius: 1
                          }}>
                            <EventIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              {project.start_date && new Date(project.start_date).toLocaleDateString()}
                              {project.start_date && project.end_date && ' - '}
                              {project.end_date && new Date(project.end_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ 
                      justifyContent: 'flex-end',
                      p: 2,
                      pt: 1,
                      borderTop: `1px solid ${theme.palette.divider}`
                    }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(project)}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleOpenDeleteDialog(project)}
                        color="error"
                        variant="outlined"
                      >
                        Löschen
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            ) : (
              // Desktop Ansicht: Tabelle
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Kunde</TableCell>
                      <TableCell>Adresse</TableCell>
                      <TableCell>Zeitraum</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>{project.name}</TableCell>
                        <TableCell>{project.client}</TableCell>
                        <TableCell>{project.address}</TableCell>
                        <TableCell>
                          {project.start_date && new Date(project.start_date).toLocaleDateString()}
                          {project.start_date && project.end_date && ' - '}
                          {project.end_date && new Date(project.end_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{renderStatusChip(project.status)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Projekt bearbeiten">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(project)}
                              color="primary"
                              aria-label={`Projekt ${project.name} bearbeiten`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Projekt löschen">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(project)}
                              color="error"
                              aria-label={`Projekt ${project.name} löschen`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Dialog für Projekt erstellen/bearbeiten */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {selectedProject ? 'Projekt bearbeiten' : 'Neues Projekt'}
          </DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="Projektname"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="client"
                    name="client"
                    label="Kunde"
                    value={formik.values.client}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    label="Adresse"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="start_date"
                    name="start_date"
                    label="Startdatum"
                    type="date"
                    value={formik.values.start_date}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="end_date"
                    name="end_date"
                    label="Enddatum"
                    type="date"
                    value={formik.values.end_date}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <Button onClick={handleCloseDialog}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={formik.isSubmitting}
              >
                {selectedProject ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Dialog für Projekt löschen */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            Projekt löschen
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchten Sie das Projekt "{selectedProject?.name}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={handleCloseDeleteDialog}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleDeleteProject}
              variant="contained" 
              color="error"
            >
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Projects; 